import * as yup from "yup"
import { CompanyDto } from "~/ui-rtk/api/types"

type KeywordsPatchInput = {
  searchAlias: string
  brands: string[]
  competitors: string[]
  categories: string[]
  /** Omit to preserve the company's existing Instagram competitor handles. */
  instagramCompetitors?: string[]
}

/**
 * The gks_v2 scraper queries the search alias (falling back to the company
 * name) as the company's own brand term, but pipeline-dbt only KEEPS brand
 * rows whose term appears in keywords.google.brands — a term missing from
 * brands[] silently drops the company's own share from the report (dashboard
 * and trend callout alike). SoS-tier keyword writes run the effective term
 * through this merge (APP-452). Deliberately NOT applied to paid companies:
 * brands[] also drives the paid branded-search classification models, whose
 * list paid users curate themselves.
 */
/**
 * The single definition of "the company's own brand term" as the gks_v2
 * scraper resolves it: the search alias, falling back to the company name
 * when the alias is empty. Use this everywhere the term is needed (keyword
 * writes, the report's trend callout) — hand-rolling the fallback at call
 * sites is how the read/write sides drifted apart once already (APP-452).
 */
export const resolveOwnBrandTerm = (
  searchAlias: string | undefined,
  companyName: string | undefined,
): string | undefined => searchAlias?.trim() || companyName

export const mergeAliasIntoBrands = (
  searchAlias: string | undefined,
  brands: string[],
): string[] => {
  const alias = searchAlias?.trim()
  if (!alias) {
    return brands
  }
  const present = brands.some(
    brand => brand.trim().toLowerCase() === alias.toLowerCase(),
  )
  return present ? brands : [alias, ...brands]
}

/**
 * Whether brands[] carries the company's own brand term — the alias, or the
 * company name when the alias is empty — compared the way mergeAliasIntoBrands
 * compares: trimmed and case-insensitive. pipeline-dbt's own equality
 * (`lower(brandTerm) = lower(term)`) does not trim, so this predicts dbt only
 * while brands[] entries carry no stray whitespace — true for every write path
 * today because ChipsInput trims each entry on add.
 *
 * The paid keyword forms validate against this instead of merging (MD-5463):
 * pipeline-dbt keeps a company's own-brand Share of Search rows only when the
 * queried term appears in brands[], nothing downstream reconciles it, and the
 * SoS settings dialog does not even show brands[], so a missing term silently
 * drops the company from its own Total and No Category views.
 */
export const brandsIncludeOwnTerm = (
  searchAlias: string | undefined,
  companyName: string | undefined,
  // Matches yup's array().of(string()) element type, so the four validators
  // pass the value straight through.
  brands: readonly (string | undefined)[] | undefined,
): boolean => {
  const term = resolveOwnBrandTerm(searchAlias, companyName)
    ?.trim()
    .toLowerCase()
  if (!term) {
    return true
  }
  return (brands ?? []).some(brand => brand?.trim().toLowerCase() === term)
}

export const ownBrandTermMissingMessage = (
  searchAlias: string | undefined,
  companyName: string | undefined,
): string =>
  `Brand Keywords must include "${resolveOwnBrandTerm(searchAlias, companyName)}". Share of Search identifies your own brand by that term, so the report leaves your brand out until the term is added.`

/**
 * yup test for a Brand Keywords array field: passes when the list carries the
 * own-brand term, otherwise fails with ownBrandTermMissingMessage. `getAlias`
 * reads the alias from the sibling form values (`this.parent`), or from a
 * closure when the form has no alias field of its own.
 */
export const ownBrandKeywordsTest = (
  companyName: string | undefined,
  getAlias: (parent: Record<string, unknown>) => string | undefined,
) =>
  function (
    this: yup.TestContext,
    brands: readonly (string | undefined)[] | undefined,
  ) {
    const alias = getAlias(this.parent)
    return brandsIncludeOwnTerm(alias, companyName, brands)
      ? true
      : this.createError({
          message: ownBrandTermMissingMessage(alias, companyName),
        })
  }

/**
 * Builds the full metadata payload for a company keywords update. The API's
 * handleKeywordsUpdate rebuilds metadata.keywords as exactly
 * { google, instagram }, so both objects must always be sent whole — a
 * partial send would drop regions/hash/Instagram competitors. Shared by the
 * standard onboarding KeywordsForm and the SoS free-tier wizard.
 */
export const buildKeywordsMetadata = (
  currentCompany: CompanyDto,
  input: KeywordsPatchInput,
): CompanyDto["metadata"] => {
  const existing = (currentCompany.metadata?.keywords ?? {}) as {
    google?: Record<string, unknown>
    instagram?: Record<string, unknown>
  }

  return {
    ...(currentCompany.metadata ?? {}),
    keywords: {
      google: {
        ...(existing.google ?? {}),
        searchAlias: input.searchAlias,
        brands: input.brands,
        competitors: input.competitors,
        categories: input.categories,
        hash: (existing.google?.hash ?? null) as string | null,
        regions: (existing.google?.regions ?? []) as string[],
      },
      instagram: {
        ...(existing.instagram ?? {}),
        competitors:
          input.instagramCompetitors ??
          ((existing.instagram?.competitors ?? []) as string[]),
        hash: (existing.instagram?.hash ?? "") as string,
      },
    },
  } as CompanyDto["metadata"]
}
